//client/src/components/WebcamButton.tsx
import React, { useState } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  Text,
  useToast,
  Box,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import Webcam from 'react-webcam';

interface WebcamButtonProps {
  onPhotoCapture: (photoUrl: string) => void;
  buttonText?: string;
  buttonSize?: 'sm' | 'md' | 'lg';
}

const videoConstraints = {
  width: 720,
  height: 720,
  facingMode: 'user',
};

const WebcamComponent = Webcam as unknown as React.ComponentType<any>;

export default function WebcamButton({
  onPhotoCapture,
  buttonText = 'Start Webcam',
  buttonSize = 'md', // Default to 'md' if undefined
}: WebcamButtonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [webcamRef, setWebcamRef] = useState<Webcam | null>(null);
  const toast = useToast();

  const handleStartWebcam = () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast({
        title: 'Error',
        description: 'Your browser does not support webcam access.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    navigator.mediaDevices.getUserMedia({ video: true })
      .then(() => onOpen())
      .catch((error) => {
        toast({
          title: 'Error',
          description: 'Could not access webcam. Please ensure you have granted camera permissions.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      });
  };

  const handleCapture = () => {
    const imageSrc = webcamRef?.getScreenshot();
    if (imageSrc) {
      onPhotoCapture(imageSrc);
      onClose();
    }
  };

  return (
    <>
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Button
          size={buttonSize || 'md'} // Fallback to 'md' if undefined
          colorScheme="purple"
          onClick={handleStartWebcam}
        >
          {buttonText}
        </Button>
      </motion.div>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Take Your Photo</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Box w="full" h="720px" borderRadius="xl" overflow="hidden" boxShadow="xl">
                {isOpen && (
                  <WebcamComponent
                    ref={(ref: Webcam | null) => setWebcamRef(ref)}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    videoConstraints={videoConstraints}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </Box>
              <Button colorScheme="purple" onClick={handleCapture} size="lg" w="full">
                Capture Photo
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}